import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { salesOrdersAPI, customersAPI, productsAPI, paymentsAPI, invoicesAPI, codSettlementsAPI } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, Eye, Loader2, ShoppingCart, CreditCard, RotateCcw, Trash2, Truck, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';

const getTodayDate = () => new Date().toLocaleDateString('en-CA');
const newRequestId = () => globalThis.crypto?.randomUUID?.() || `cod-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-LK', {
    style: 'currency',
    currency: 'LKR',
    minimumFractionDigits: 0,
  }).format(amount);
};

const statusColors = {
  pending: 'badge-warning',
  pending_payment: 'badge-warning',
  return_pending_confirmation: 'badge-warning',
  cancellation_requested: 'badge-warning',
  processing: 'badge-info',
  packed: 'badge-info',
  shipped: 'badge-info',
  completed: 'badge-success',
  cancelled: 'badge-error',
  returned: 'badge-error',
};

const paymentColors = {
  unpaid: 'badge-error',
  pending: 'badge-warning',
  failed: 'badge-error',
  cancelled: 'badge-error',
  chargedback: 'badge-error',
  refunded: 'badge-error',
  partial: 'badge-warning',
  paid: 'badge-success',
};

const deliveryColors = {
  ready_for_pickup: 'bg-indigo-100 text-indigo-800',
  collected: 'bg-green-100 text-green-800',
  pending: 'badge-warning',
  processing: 'badge-info',
  packed: 'badge-info',
  shipped: 'badge-info',
  delivered: 'badge-success',
  on_hold: 'badge-warning',
  cancelled: 'badge-error',
  returned: 'badge-error',
};

export const SalesOrders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialOrderFilter = searchParams.get('stage') || searchParams.get('status') || 'all';
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(initialOrderFilter);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [variations, setVariations] = useState([]);
  const [loadingVariations, setLoadingVariations] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [createdPaymentLink, setCreatedPaymentLink] = useState('');
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  const [formData, setFormData] = useState({
    customer_id: '',
    items: [],
    coupon_code: '',
    shipping_method_id: 'sri-lanka-post',
    payment_option: 'bank_transfer',
    order_source: 'erp', fulfillment_method: 'courier',
    notes: '',
  });

  const [newItem, setNewItem] = useState({
    product_id: '',
    variation_id: '',
    quantity: 1,
  });

  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_date: getTodayDate(),
    payment_method: 'bank',
    bank_account_id: '',
    settlement_reference: '',
    notes: '',
  });

  const [refundData, setRefundData] = useState({
    amount: '', refund_date: getTodayDate(), refund_method: 'bank_transfer',
    bank_account_id: '', refund_reference: '', notes: '', client_request_id: '',
  });

  const [deliveryData, setDeliveryData] = useState({
    delivery_status: 'pending',
    courier: '',
    tracking_number: '',
    tracking_url: '',
    delivery_notes: '',
  });

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') {
        if (['pending', 'processing', 'packed', 'shipped', 'delivered'].includes(statusFilter)) {
          params.stage = statusFilter;
        } else {
          params.status = statusFilter;
        }
      }

      const [ordersRes, customersRes, productsRes, bankAccountsRes] = await Promise.all([
        salesOrdersAPI.getAll(params),
        customersAPI.getAll(),
        productsAPI.getAll({ all: true }),
        api.get('/bank-accounts'),
      ]);
      setOrders(ordersRes.data);
      setCustomers(customersRes.data);
      setProducts(productsRes.data);
      setBankAccounts(bankAccountsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    if (value === 'all') {
      setSearchParams({}, { replace: true });
    } else if (['pending', 'processing', 'packed', 'shipped', 'delivered'].includes(value)) {
      setSearchParams({ stage: value }, { replace: true });
    } else {
      setSearchParams({ status: value }, { replace: true });
    }
  };

  const requestQuote = async (nextForm) => {
    if (!nextForm.customer_id || nextForm.items.length === 0) {
      setQuote(null);
      return null;
    }
    setQuoteLoading(true);
    try {
      const response = await salesOrdersAPI.quote({
        customer_id: nextForm.customer_id,
        items: nextForm.items.map(({ product_id, variation_id, quantity }) => ({ product_id, variation_id: variation_id || null, quantity: Number(quantity) })),
        coupon_code: nextForm.coupon_code?.trim() || null,
        shipping_method_id: nextForm.shipping_method_id,
        fulfillment_method: nextForm.fulfillment_method,
      });
      setQuote(response.data);
      return response.data;
    } catch (error) {
      setQuote(null);
      toast.error(error.response?.data?.detail || 'Could not calculate order total');
      return null;
    } finally {
      setQuoteLoading(false);
    }
  };

  const handleProductChange = async (productId) => {
    const product = products.find((entry) => entry.id === productId);
    setNewItem({ product_id: productId, variation_id: '', quantity: 1 });
    setVariations([]);
    if (product?.product_type !== 'variable') return;
    setLoadingVariations(true);
    try {
      const response = await productsAPI.getVariations(productId);
      setVariations((response.data.variations || []).filter((variation) => Number(variation.stock_quantity || 0) > 0));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load product variations');
    } finally {
      setLoadingVariations(false);
    }
  };

  const handleAddItem = async () => {
    const product = products.find((p) => p.id === newItem.product_id);
    if (!product) return;
    const variation = variations.find((entry) => entry.id === newItem.variation_id);
    if (product.product_type === 'variable' && !variation) {
      toast.error('Select a size/variation');
      return;
    }
    const quantity = parseInt(newItem.quantity, 10);
    if (!quantity || quantity < 1) return;
    const price = variation
      ? (Number(variation.sale_price) > 0 ? Number(variation.sale_price) : Number(variation.regular_price || 0))
      : Number(product.sale_price) > 0 ? Number(product.sale_price) : Number(product.selling_price || product.regular_price || 0);
    const options = (variation?.attributes || []).map((attribute) => attribute.option).join(' / ');

    const item = {
      product_id: product.id,
      variation_id: variation?.id || null,
      product_name: options ? `${product.name} - ${options}` : product.name,
      sku: variation?.sku || product.sku || '',
      quantity,
      unit_price: price,
      total: price * quantity,
    };
    const nextForm = { ...formData, items: [...formData.items, item] };
    setFormData(nextForm);
    setNewItem({ product_id: '', variation_id: '', quantity: 1 });
    setVariations([]);
    await requestQuote(nextForm);
  };

  const handleRemoveItem = async (index) => {
    const nextForm = { ...formData, items: formData.items.filter((_, i) => i !== index) };
    setFormData(nextForm);
    await requestQuote(nextForm);
  };

  const calculateTotal = () => {
    return quote?.total ?? formData.items.reduce((sum, item) => sum + item.total, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    setSubmitting(true);
    try {
      const response = await salesOrdersAPI.create(formData);
      toast.success('Sales order created successfully');
      if (response.data.payment_link) {
        setCreatedPaymentLink(response.data.payment_link);
      } else {
        setDialogOpen(false);
      }
      setFormData({ customer_id: '', items: [], coupon_code: '', shipping_method_id: 'sri-lanka-post', payment_option: 'bank_transfer', order_source: 'erp', fulfillment_method: 'courier', notes: '' });
      setQuote(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewOrder = async (order) => {
    try {
      const response = await salesOrdersAPI.getOne(order.id);
      setSelectedOrder(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };

  const handleInvoice = async (orderId, download = false) => {
    // Open during the click event, before awaiting the authenticated request.
    const preview = download ? null : window.open('about:blank', '_blank');
    if (preview) {
      preview.opener = null;
      preview.document.title = 'Loading invoice...';
      preview.document.body.textContent = 'Loading invoice...';
    }
    setInvoiceLoading(true);
    try {
      const response = await invoicesAPI.getPdf(orderId);
      if (!response.data?.size) throw new Error('The invoice PDF was empty. Please retry.');
      const url = URL.createObjectURL(response.data);
      if (download || !preview || preview.closed) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `${selectedOrder?.order_number || 'invoice'}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        if (!download) toast.info('Invoice downloaded because the preview window was blocked or closed.');
      } else {
        preview.location.replace(url);
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      if (preview && !preview.closed) preview.close();
      let detail = error.response?.data?.detail;
      if (error.response?.data instanceof Blob) {
        try { detail = JSON.parse(await error.response.data.text()).detail; } catch (_) { /* Use the fallback below. */ }
      }
      toast.error(typeof detail === 'string' ? detail : (error.message || 'Could not generate invoice PDF'));
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentData.bank_account_id) {
      toast.error('Please select an account to receive payment');
      return;
    }
    setSubmitting(true);
    try {
      if (selectedOrder.payment_option === 'cod') {
        await codSettlementsAPI.create(selectedOrder.id, {
          client_request_id: paymentData.client_request_id,
          fee_amount: 0,
          amount: parseFloat(paymentData.amount),
          payment_date: paymentData.payment_date,
          payment_method: paymentData.payment_method,
          bank_account_id: paymentData.bank_account_id,
          settlement_reference: paymentData.settlement_reference,
          notes: paymentData.notes,
        });
        toast.success('COD settlement recorded and posted to Finance');
      } else {
        await paymentsAPI.create({
          reference_type: 'sales_order',
          reference_id: selectedOrder.id,
          amount: parseFloat(paymentData.amount),
          payment_date: paymentData.payment_date,
          payment_method: paymentData.payment_method,
          bank_account_id: paymentData.bank_account_id,
          notes: paymentData.notes,
        });
        toast.success('Payment recorded successfully');
      }
      setPaymentDialogOpen(false);
      setPaymentData({ amount: '', payment_date: getTodayDate(), payment_method: 'bank', bank_account_id: '', settlement_reference: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturn = async (order) => {
    const confirming = order.status === 'return_pending_confirmation';
    if (!window.confirm(confirming ? 'Confirm that the returned parcel was inspected and can be restored to inventory?' : 'Record this order as returned?')) return;
    try {
      await salesOrdersAPI.return(order.id, confirming);
      toast.success(confirming ? 'Returned inventory confirmed' : 'Return recorded; confirm inventory after inspection');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to return order');
    }
  };

  const openRefundDialog = (order) => {
    const remaining = Math.max(0, Number(order.paid_amount || 0) - Number(order.refunded_amount || 0));
    setSelectedOrder(order);
    setRefundData({
      amount: remaining.toFixed(2), refund_date: getTodayDate(),
      refund_method: order.payment_method === 'payhere' || order.payment_option === 'payment_link' ? 'payhere' : (order.payment_option === 'cash' ? 'cash' : 'bank_transfer'),
      bank_account_id: '', refund_reference: '', notes: '', client_request_id: newRequestId(),
    });
    setRefundDialogOpen(true);
  };

  const handleCompleteRefund = async () => {
    const amount = Number(refundData.amount || 0);
    const remaining = Math.max(0, Number(selectedOrder?.paid_amount || 0) - Number(selectedOrder?.refunded_amount || 0));
    if (!(amount > 0) || amount > remaining) {
      toast.error(`Refund amount must be between LKR 0.01 and ${formatCurrency(remaining)}`);
      return;
    }
    if (!refundData.bank_account_id || !refundData.refund_date || !refundData.refund_reference.trim()) {
      toast.error('Refund date, account and reference are required');
      return;
    }
    setSubmitting(true);
    try {
      await salesOrdersAPI.completeRefund(selectedOrder.id, { ...refundData, amount });
      toast.success(amount === remaining ? 'Refund completed successfully' : 'Partial refund recorded successfully');
      setRefundDialogOpen(false);
      setViewDialogOpen(false);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete refund');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectCancellation = async () => {
    const reason = window.prompt('Reason for declining the cancellation request:');
    if (!reason?.trim()) return;
    if (reason.trim().length < 3) {
      toast.error('Please enter a clear reason');
      return;
    }
    setSubmitting(true);
    try {
      await salesOrdersAPI.rejectCancellation(selectedOrder.id, reason.trim());
      toast.success('Cancellation request declined');
      setViewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve cancellation request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveCancellation = async () => {
    if (!window.confirm('Cancel this paid order and automatically request a full PayHere refund?')) return;
    setSubmitting(true);
    try {
      await salesOrdersAPI.cancel(selectedOrder.id);
      toast.success('Order refunded and cancelled');
      setViewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve cancellation request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelOrder = async (order) => {
    const paid = order.payment_status === 'paid';
    const message = paid
      ? 'Cancel this order and automatically refund the full PayHere payment?'
      : 'Cancel this order and release any reserved stock?';
    if (!window.confirm(message)) return;
    setSubmitting(true);
    try {
      await salesOrdersAPI.cancel(order.id);
      toast.success(paid ? 'Order cancelled and refunded' : 'Order cancelled');
      setViewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    } finally { setSubmitting(false); }
  };

  const openDeliveryDialog = (order) => {
    setSelectedOrder(order);
    setDeliveryData({
      delivery_status: order.delivery_status || 'pending',
      courier: order.courier || order.shipping_method?.name || '',
      tracking_number: order.tracking_number || '',
      tracking_url: order.tracking_url || '',
      delivery_notes: order.delivery_notes || '',
    });
    setDeliveryDialogOpen(true);
  };

  const handleDeliveryUpdate = async () => {
    if (deliveryData.delivery_status === 'shipped' && (!deliveryData.courier.trim() || !deliveryData.tracking_number.trim())) {
      toast.error('Courier and tracking number are required when an order is shipped');
      return;
    }
    setSubmitting(true);
    try {
      await salesOrdersAPI.update(selectedOrder.id, deliveryData);
      toast.success('Delivery status updated');
      setDeliveryDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update delivery status');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="sales-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Sales Orders
          </h2>
          <p className="text-slate-500 mt-1">{orders.length} orders</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="gap-2 bg-indigo-600 hover:bg-indigo-700" data-testid="create-order-btn">
          <Plus className="w-4 h-4" />
          New Order
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
            <SelectTrigger className="w-48" data-testid="status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="pending_payment">Pending Payment</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="packed">Packed</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="returned">Returned</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No orders found</h3>
              <p className="text-slate-500 mt-1">Create your first sales order.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead className="table-header-cell">Order #</TableHead>
                  <TableHead className="table-header-cell">Customer</TableHead>
                  <TableHead className="table-header-cell">Date</TableHead>
                  <TableHead className="table-header-cell text-right">Total</TableHead>
                  <TableHead className="table-header-cell">Status</TableHead>
                  <TableHead className="table-header-cell">Payment</TableHead>
                  <TableHead className="table-header-cell">Delivery</TableHead>
                  <TableHead className="table-header-cell w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id} className="table-row" data-testid={`order-row-${order.id}`}>
                    <TableCell className="table-cell font-medium">{order.order_number}</TableCell>
                    <TableCell className="table-cell">{order.customer_name}</TableCell>
                    <TableCell className="table-cell text-slate-500">
                      {new Date(order.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="table-cell text-right font-medium">{formatCurrency(order.total)}</TableCell>
                    <TableCell className="table-cell">
                      <Badge className={statusColors[order.status]}>{order.status}</Badge>
                    </TableCell>
                    <TableCell className="table-cell">
                      <Badge className={paymentColors[order.payment_status]}>{order.payment_status}</Badge>
                    </TableCell>
                    <TableCell className="table-cell">
                      <Badge className={deliveryColors[order.delivery_status || 'pending']}>{order.delivery_status || 'pending'}</Badge>
                    </TableCell>
                    <TableCell className="table-cell">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewOrder(order)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openDeliveryDialog(order)}>
                            <Truck className="w-4 h-4 mr-2" />
                            {order.fulfillment_method === 'store_pickup' ? 'Update Pickup' : 'Update Delivery'}
                          </DropdownMenuItem>
                          {order.source !== 'storefront' && !['paid', 'refunded'].includes(order.payment_status) && order.status !== 'returned' && (order.payment_option !== 'cod' || order.delivery_status === 'delivered') && (
                            <DropdownMenuItem onClick={() => {
                              setSelectedOrder(order);
                              setPaymentData({ amount: (order.total - order.paid_amount).toString(), payment_date: getTodayDate(), payment_method: order.payment_option === 'cash' ? 'cash' : 'bank', bank_account_id: '', settlement_reference: '', notes: '', client_request_id: newRequestId() });
                              setPaymentDialogOpen(true);
                            }}>
                              <CreditCard className="w-4 h-4 mr-2" />
                              {order.payment_option === 'cod' ? 'Record COD Settlement' : 'Record Payment'}
                            </DropdownMenuItem>
                          )}
                          {!['cancelled', 'completed', 'returned'].includes(order.status) && !['shipped', 'delivered', 'collected', 'cancelled'].includes(order.delivery_status || 'pending') && (
                            <DropdownMenuItem className="text-red-600" onClick={() => handleCancelOrder(order)}>
                              Cancel Order{order.payment_status === 'paid' ? ' & Refund' : ''}
                            </DropdownMenuItem>
                          )}
                          {order.source !== 'storefront' && order.status !== 'returned' && (
                            <DropdownMenuItem className="text-amber-600" onClick={() => handleReturn(order)}>
                              <RotateCcw className="w-4 h-4 mr-2" />
                              {order.status === 'return_pending_confirmation' ? 'Confirm Returned Inventory' : 'Return Order'}
                            </DropdownMenuItem>
                          )}
                          {['returned', 'cancelled'].includes(order.status) && ['manual_pending', 'pending', 'partially_refunded'].includes(order.refund_status) && Number(order.paid_amount || 0) > Number(order.refunded_amount || 0) && (
                            <DropdownMenuItem className="text-purple-700" onClick={() => openRefundDialog(order)}>
                              <CreditCard className="w-4 h-4 mr-2" />
                              Complete Refund
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Order Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl w-full" data-testid="create-order-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Create Sales Order</DialogTitle>
            <DialogDescription>Add items to create a new sales order</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Order source</Label>
                  <Select value={formData.order_source} onValueChange={(v) => { const next = { ...formData, order_source: v, fulfillment_method: v === 'walk_in' ? 'store_pickup' : formData.fulfillment_method, payment_option: v === 'walk_in' ? 'cash' : (v === 'erp' && formData.payment_option === 'cod' ? 'bank_transfer' : formData.payment_option) }; setFormData(next); requestQuote(next); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="erp">ERP manual order</SelectItem>
                      <SelectItem value="walk_in">Walk-in / In-store</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="facebook">Facebook</SelectItem>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="other_social">Other social media</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Fulfilment method</Label>
                <Select value={formData.fulfillment_method} onValueChange={(v) => { const next = { ...formData, fulfillment_method: v, payment_option: (v === 'store_pickup' && formData.payment_option === 'cod') || (v === 'courier' && formData.payment_option === 'cash') ? 'bank_transfer' : formData.payment_option }; setFormData(next); requestQuote(next); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="courier">Courier delivery</SelectItem><SelectItem value="store_pickup">Store Pickup — LKR 0 shipping</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Customer *</Label>
                <Select value={formData.customer_id} onValueChange={(v) => {
                  const nextForm = { ...formData, customer_id: v };
                  setFormData(nextForm);
                  requestQuote(nextForm);
                }}>
                  <SelectTrigger data-testid="select-customer">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Add Item */}
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Add Product</Label>
                <div className="grid grid-cols-12 gap-2">
                  <div className={products.find((p) => p.id === newItem.product_id)?.product_type === 'variable' ? 'col-span-4' : 'col-span-7'}>
                    <Select value={newItem.product_id} onValueChange={handleProductChange}>
                      <SelectTrigger data-testid="select-product">
                        <SelectValue placeholder="Select product" />
                      </SelectTrigger>
                      <SelectContent className="max-w-[500px]">
                        {products.filter((p) => p.visibility === 'public' && Number(p.stock_quantity || 0) > 0).map((p) => (
                          <SelectItem key={p.id} value={p.id} className="max-w-full">
                            <span className="truncate block max-w-[400px]" title={`${p.name} - ${formatCurrency(p.selling_price)}`}>
                              {p.name.length > 45 ? p.name.substring(0, 45) + '...' : p.name}{p.product_type === 'variable' ? ' — select size' : ` - ${formatCurrency(p.sale_price || p.selling_price)}`}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {products.find((p) => p.id === newItem.product_id)?.product_type === 'variable' && (
                    <div className="col-span-3">
                      <Select value={newItem.variation_id} onValueChange={(v) => setNewItem({ ...newItem, variation_id: v })} disabled={loadingVariations}>
                        <SelectTrigger data-testid="select-variation">
                          <SelectValue placeholder={loadingVariations ? 'Loading...' : 'Select size'} />
                        </SelectTrigger>
                        <SelectContent>
                          {variations.map((variation) => {
                            const label = (variation.attributes || []).map((attribute) => `${attribute.name}: ${attribute.option}`).join(' / ');
                            const price = Number(variation.sale_price) > 0 ? variation.sale_price : variation.regular_price;
                            return <SelectItem key={variation.id} value={variation.id}>{label} — {formatCurrency(price)} ({variation.stock_quantity} in stock)</SelectItem>;
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="col-span-2">
                    <Input
                      type="number"
                      min="1"
                      value={newItem.quantity}
                      onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                      placeholder="Qty"
                      data-testid="item-quantity"
                    />
                  </div>
                  <div className="col-span-3">
                    <Button type="button" onClick={handleAddItem} variant="outline" className="w-full" data-testid="add-item-btn">
                      Add
                    </Button>
                  </div>
                </div>
              </div>

              {/* Items List */}
              {formData.items.length > 0 && (
                <div className="border rounded-lg overflow-hidden overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[250px]">Product</TableHead>
                        <TableHead className="text-right w-28">Price</TableHead>
                        <TableHead className="text-right w-20">Qty</TableHead>
                        <TableHead className="text-right w-28">Discount</TableHead>
                        <TableHead className="text-right w-28">Net total</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="max-w-[300px]">
                            <span className="truncate block" title={item.product_name}>{item.product_name}</span>
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell className="text-right">{item.quantity}</TableCell>
                          <TableCell className="text-right text-emerald-700">{Number(quote?.items?.[index]?.discount || 0) > 0 ? `-${formatCurrency(quote.items[index].discount)}` : '—'}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(quote?.items?.[index]?.net_total ?? item.total)}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" onClick={() => handleRemoveItem(index)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Coupon code</Label>
                  <div className="flex gap-2">
                    <Input value={formData.coupon_code} onChange={(e) => setFormData({ ...formData, coupon_code: e.target.value.toUpperCase() })} placeholder="Enter coupon" data-testid="order-coupon" />
                    <Button type="button" variant="outline" disabled={quoteLoading || !formData.coupon_code.trim()} onClick={() => requestQuote(formData)}>Apply</Button>
                  </div>
                  {quote?.coupon_message && <p className="text-xs text-emerald-600">{quote.coupon_message}</p>}
                </div>
                <div className="space-y-2">
                  <Label>{formData.fulfillment_method === 'store_pickup' ? 'Store Pickup — no courier required' : formData.payment_option === 'cod' ? 'COD courier partner *' : 'Shipping service'}</Label>
                  {formData.fulfillment_method === 'store_pickup' ? <p className="text-sm">Customer collects in store — LKR 0</p> : (
                  <Select disabled={formData.fulfillment_method === 'store_pickup'} value={formData.shipping_method_id} onValueChange={(v) => {
                    const nextForm = { ...formData, shipping_method_id: v };
                    setFormData(nextForm);
                    requestQuote(nextForm);
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sri-lanka-post">Sri Lanka Post (2-4 days)</SelectItem>
                      <SelectItem value="trans-express">Trans Express (1-3 days)</SelectItem>
                    </SelectContent>
                  </Select>
                  )}
                  {quote?.shipping && <p className="text-xs text-slate-500">{quote.total_weight_kg} kg · {formData.fulfillment_method === 'store_pickup' ? 'Store Pickup — LKR 0' : quote.shipping.is_free ? 'FREE shipping' : formatCurrency(quote.shipping.cost)}{formData.payment_option === 'cod' ? ' · courier collects COD' : ''}</p>}
                </div>
                <div className="space-y-2">
                  <Label>Payment option</Label>
                  <Select value={formData.payment_option} onValueChange={(v) => setFormData({ ...formData, payment_option: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                      <SelectItem value="payment_link">PayHere payment link</SelectItem>
                      {formData.fulfillment_method === 'store_pickup' && <SelectItem value="cash">Cash</SelectItem>}
                      {formData.fulfillment_method !== 'store_pickup' && !['erp', 'walk_in'].includes(formData.order_source) && <SelectItem value="cod">Cash on Delivery</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end">
                <div className="w-full max-w-xs space-y-1 text-right">
                  {quote && <>
                    <p className="text-sm"><span className="text-slate-500">Subtotal:</span> {formatCurrency(quote.subtotal)}</p>
                    <p className="text-sm"><span className="text-slate-500">Discount:</span> -{formatCurrency(quote.discount)}</p>
                    <p className="text-sm"><span className="text-slate-500">Shipping:</span> {formData.fulfillment_method === 'store_pickup' ? formatCurrency(0) : quote.shipping.is_free ? 'FREE' : formatCurrency(quote.shipping.cost)}</p>
                  </>}
                  <p className="text-sm text-slate-500">Total</p>
                  <p className="text-2xl font-bold">{quoteLoading ? 'Calculating…' : formatCurrency(calculateTotal())}</p>
                </div>
              </div>

              {createdPaymentLink && (
                <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4">
                  <p className="font-medium text-emerald-900">Customer payment link created</p>
                  <div className="mt-2 flex gap-2">
                    <Input readOnly value={createdPaymentLink} />
                    <Button type="button" onClick={async () => { await navigator.clipboard.writeText(createdPaymentLink); toast.success('Payment link copied'); }}>Copy</Button>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setCreatedPaymentLink(''); }}>Close</Button>
              {!createdPaymentLink && <Button type="submit" disabled={submitting || quoteLoading || !formData.customer_id || !quote} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-order-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Order
              </Button>}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Order Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              Order {selectedOrder?.order_number}
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Customer</p>
                  <p className="font-medium">{selectedOrder.customer?.name || selectedOrder.customer_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Date</p>
                  <p className="font-medium">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <Badge className={statusColors[selectedOrder.status]}>{selectedOrder.status}</Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Payment</p>
                  <Badge className={paymentColors[selectedOrder.payment_status]}>{selectedOrder.payment_status}</Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Delivery</p>
                  <Badge className={deliveryColors[selectedOrder.delivery_status || 'pending']}>{selectedOrder.delivery_status || 'pending'}</Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Source</p>
                  <p className="font-medium capitalize">{selectedOrder.order_source === 'walk_in' ? 'Walk-in / In-store' : selectedOrder.order_source || selectedOrder.source || 'ERP'}{selectedOrder.fulfillment_method === 'store_pickup' ? ' · Store Pickup' : ''}</p>
                </div>
              </div>

              {(selectedOrder.courier || selectedOrder.tracking_number || selectedOrder.payment_option === 'cod') && (
                <div className="rounded-lg border bg-slate-50 p-4">
                  <p className="text-sm font-medium text-slate-900">Courier: {selectedOrder.courier || selectedOrder.shipping_method?.name || 'Not assigned'}</p>
                  <p className="mt-1 text-sm text-slate-600">Tracking: {selectedOrder.tracking_number || 'Not assigned'}</p>
                  {selectedOrder.tracking_url && <a href={selectedOrder.tracking_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-sm font-medium text-blue-600 hover:underline">Open tracking page</a>}
                  {selectedOrder.delivery_notes && <p className="mt-1 text-sm text-slate-600">{selectedOrder.delivery_notes}</p>}
                </div>
              )}

              {selectedOrder.cancellation_status === 'requested' && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
                  <p className="font-semibold">Customer cancellation requested</p>
                  <p className="mt-1 text-sm">{selectedOrder.cancellation_reason || 'No reason was provided.'}</p>
                  <p className="mt-2 text-xs text-amber-700">
                    Packing and shipping are blocked until this request is resolved. Complete the PayHere refund before approving cancellation.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" disabled={submitting} onClick={handleRejectCancellation}>Decline request</Button>
                    <Button size="sm" disabled={submitting} onClick={handleApproveCancellation} className="bg-amber-600 hover:bg-amber-700">Refund completed — cancel</Button>
                  </div>
                </div>
              )}

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Discount</TableHead>
                      <TableHead className="text-right">Net total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedOrder.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right text-emerald-700">{Number(item.discount || 0) > 0 ? `-${formatCurrency(item.discount)}` : '—'}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(item.net_total ?? item.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end">
                <div className="text-right space-y-1">
                  <p className="text-sm"><span className="text-slate-500">Merchandise subtotal:</span> {formatCurrency(selectedOrder.subtotal)}</p>
                  {Number(selectedOrder.discount || 0) > 0 && (
                    <>
                      <p className="text-sm text-emerald-700"><span className="text-slate-500">Coupon discount{selectedOrder.coupon_code ? ` (${selectedOrder.coupon_code})` : ''}:</span> -{formatCurrency(selectedOrder.discount)}</p>
                      <p className="text-sm"><span className="text-slate-500">After discount:</span> {formatCurrency(selectedOrder.subtotal_after_discount ?? (Number(selectedOrder.subtotal || 0) - Number(selectedOrder.discount || 0)))}</p>
                    </>
                  )}
                  {Number(selectedOrder.shipping_discount || 0) > 0 ? (
                    <>
                      <p className="text-sm"><span className="text-slate-500">Standard shipping:</span> {formatCurrency(selectedOrder.shipping_before_discount || 0)}</p>
                      <p className="text-sm text-emerald-700"><span className="text-slate-500">Shipping discount{selectedOrder.coupon_code ? ` (${selectedOrder.coupon_code})` : ''}:</span> -{formatCurrency(selectedOrder.shipping_discount)}</p>
                    </>
                  ) : <p className="text-sm"><span className="text-slate-500">Shipping:</span> {Number(selectedOrder.shipping || 0) > 0 ? formatCurrency(selectedOrder.shipping) : 'Free'}</p>}
                  <p className="text-lg font-bold"><span className="text-slate-500">Total:</span> {formatCurrency(selectedOrder.total)}</p>
                  <p className="text-sm text-emerald-600"><span className="text-slate-500">Paid:</span> {formatCurrency(selectedOrder.paid_amount)}</p>
                  <p className="text-sm text-amber-600"><span className="text-slate-500">Balance:</span> {formatCurrency(selectedOrder.total - selectedOrder.paid_amount)}</p>
                </div>
              </div>
              <div className="flex flex-wrap justify-start gap-2 border-t pt-4">
                <Button type="button" variant="outline" disabled={invoiceLoading} onClick={() => handleInvoice(selectedOrder.id, false)} className="gap-2">
                  <FileText className="w-4 h-4" /> View PDF Invoice
                </Button>
                <Button type="button" variant="outline" disabled={invoiceLoading} onClick={() => handleInvoice(selectedOrder.id, true)} className="gap-2">
                  <Download className="w-4 h-4" /> Download PDF
                </Button>
                {['returned', 'cancelled'].includes(selectedOrder.status) && ['manual_pending', 'pending', 'partially_refunded'].includes(selectedOrder.refund_status) && Number(selectedOrder.paid_amount || 0) > Number(selectedOrder.refunded_amount || 0) && (
                  <Button type="button" onClick={() => openRefundDialog(selectedOrder)} className="bg-purple-600 hover:bg-purple-700 gap-2">
                    <CreditCard className="w-4 h-4" /> Complete Refund
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Complete Customer Refund Dialog */}
      <Dialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Customer Refund</DialogTitle>
            <DialogDescription>{selectedOrder?.order_number} — refund only after money has been returned to the customer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm text-purple-900">
              Refund remaining: <strong>{formatCurrency(Math.max(0, Number(selectedOrder?.paid_amount || 0) - Number(selectedOrder?.refunded_amount || 0)))}</strong>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Refund amount (LKR) *</Label><Input type="number" min="0.01" step="0.01" value={refundData.amount} onChange={(e) => setRefundData({ ...refundData, amount: e.target.value })} /></div>
              <div className="space-y-2"><Label>Refund date *</Label><Input type="date" max={getTodayDate()} value={refundData.refund_date} onChange={(e) => setRefundData({ ...refundData, refund_date: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Paid from account *</Label><Select value={refundData.bank_account_id} onValueChange={(v) => setRefundData({ ...refundData, bank_account_id: v })}><SelectTrigger><SelectValue placeholder="Select bank or cash account" /></SelectTrigger><SelectContent>{bankAccounts.filter((account) => account.is_active !== false && ['bank', 'cash'].includes(account.account_type)).map((account) => <SelectItem key={account.id} value={account.id}>{account.account_name || account.name} — {formatCurrency(account.current_balance || 0)}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Refund method *</Label><Select value={refundData.refund_method} onValueChange={(v) => setRefundData({ ...refundData, refund_method: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="bank_transfer">Bank Transfer</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="payhere">PayHere</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>Refund reference / receipt number *</Label><Input value={refundData.refund_reference} maxLength={120} onChange={(e) => setRefundData({ ...refundData, refund_reference: e.target.value })} placeholder="Bank reference, PayHere refund ID or cash receipt" /></div>
            <div className="space-y-2"><Label>Notes</Label><Input value={refundData.notes} maxLength={500} onChange={(e) => setRefundData({ ...refundData, notes: e.target.value })} placeholder="Optional refund note" /></div>
            <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900"><strong>This will:</strong><br />• Deduct the amount from the selected bank/cash account<br />• Reduce 2440 – Refunds Payable<br />• Mark the order Refunded when the full amount is completed</div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setRefundDialogOpen(false)}>Cancel</Button><Button onClick={handleCompleteRefund} disabled={submitting || !refundData.amount || !refundData.bank_account_id || !refundData.refund_date || !refundData.refund_reference.trim()} className="bg-purple-600 hover:bg-purple-700">{submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Complete Refund</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery Status Dialog */}
      <Dialog open={deliveryDialogOpen} onOpenChange={setDeliveryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{selectedOrder?.fulfillment_method === 'store_pickup' ? 'Update Pickup' : 'Update Delivery'}</DialogTitle>
            <DialogDescription>{selectedOrder?.order_number} — status and tracking details shown to the customer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Delivery Status</Label>
              <Select value={deliveryData.delivery_status} onValueChange={(value) => setDeliveryData({ ...deliveryData, delivery_status: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  {selectedOrder?.fulfillment_method === 'store_pickup' ? <><SelectItem value="ready_for_pickup">Ready for Pickup</SelectItem><SelectItem value="collected">Collected</SelectItem></> : <><SelectItem value="packed">Packed</SelectItem><SelectItem value="shipped">Shipped</SelectItem><SelectItem value="delivered">Delivered</SelectItem></>}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Courier {deliveryData.delivery_status === 'shipped' ? '*' : ''}</Label>
              <Input disabled={selectedOrder?.fulfillment_method === 'store_pickup'} value={deliveryData.courier} onChange={(e) => setDeliveryData({ ...deliveryData, courier: e.target.value })} placeholder="Sri Lanka Post / Trans Express" />
            </div>
            <div className="space-y-2">
              <Label>Tracking Number {deliveryData.delivery_status === 'shipped' ? '*' : ''}</Label>
              <Input disabled={selectedOrder?.fulfillment_method === 'store_pickup'} value={deliveryData.tracking_number} onChange={(e) => setDeliveryData({ ...deliveryData, tracking_number: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Tracking Link (optional)</Label>
              <Input type="url" placeholder="https://courier.example/track/..." disabled={selectedOrder?.fulfillment_method === 'store_pickup'} value={deliveryData.tracking_url || ''} onChange={(e) => setDeliveryData({ ...deliveryData, tracking_url: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Delivery Notes</Label>
              <Input value={deliveryData.delivery_notes} onChange={(e) => setDeliveryData({ ...deliveryData, delivery_notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeliveryDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDeliveryUpdate} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Delivery Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{selectedOrder?.payment_option === 'cod' ? 'Record COD Settlement' : 'Record Payment'}</DialogTitle>
            <DialogDescription>
              Balance due: {formatCurrency(selectedOrder?.total - selectedOrder?.paid_amount || 0)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Amount (LKR) *</Label>
              <Input
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                data-testid="payment-amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Date *</Label>
              <Input
                type="date"
                value={paymentData.payment_date}
                onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                data-testid="payment-date"
              />
              <p className="text-xs text-gray-500">
                This date is used for the bank/cash transaction and General Ledger posting.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Payment Received To *</Label>
              <Select 
                value={paymentData.bank_account_id} 
                onValueChange={(v) => setPaymentData({ ...paymentData, bank_account_id: v })}
              >
                <SelectTrigger data-testid="payment-account-select">
                  <SelectValue placeholder="Select receiving account" />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.account_name} ({acc.account_type === 'cash' ? 'Cash' : acc.bank_name}) - {formatCurrency(acc.current_balance)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Select the bank or cash account where payment was received
              </p>
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={paymentData.payment_method} onValueChange={(v) => setPaymentData({ ...paymentData, payment_method: v })}>
                <SelectTrigger data-testid="payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank">Bank Transfer</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="online">Online Payment</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={paymentData.notes}
                onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                placeholder="Payment reference, cheque number, etc."
              />
            </div>
            {selectedOrder?.payment_option === 'cod' && <div className="space-y-2">
              <Label>Courier statement / payment reference</Label>
              <Input
                value={paymentData.settlement_reference || ''}
                onChange={(e) => setPaymentData({ ...paymentData, settlement_reference: e.target.value })}
                placeholder="Reference is required unless Notes explains the receipt"
                maxLength={120}
              />
            </div>}
            <div className="bg-green-50 p-3 rounded-lg text-sm text-green-800">
              <strong>This will:</strong><br />
              • Increase selected account balance<br />
              • Reduce Accounts Receivable<br />
              • Create journal entry in General Ledger
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment} disabled={submitting || !paymentData.amount || !paymentData.payment_date || !paymentData.bank_account_id || (selectedOrder?.payment_option === 'cod' && !paymentData.settlement_reference?.trim() && !paymentData.notes?.trim())} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-payment-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesOrders;
